import { useMemo } from 'react';

import ActivityPrint from '../components/activity/activity-print';
import {
  ActivitiesContentType,
  LANGUAGE_CODE_EN_AU,
  LanguageCodes,
} from 'global/constants';
import {
  usePagePreviewIntersectionObserver,
  usePagePreviewResizeObserver,
} from './activity-builder-page-preview.hooks';
import { useReadSectionFromCache } from '../present/activity-present.hooks';
import { useGetActivitySections } from '../activity-builder-section.hooks';
import { getSectionsAndSubSections } from '../activity-builder.utils';
import { getContentHeight } from './activity-builder-page-preview.utils';
import { PagePreviewContent } from './activity-builder-page-preview-shared';
import ActivityAnswersPrintHeader from '../print/answers/activity-answers-print-header';
import { ActivityBuilderIntroFooter } from '../introduction/activity-builder-intro';
import ActivityAnswersPrintComponents from '../print/answers/activity-answers-print-components';
import { HeaderFooterContentBase } from './activity-builder-page-preview';
import { BaseSectionOverview } from '../activity-builder.constants';
import { PREVIEW } from '../print/answers/activity-answers-print.constants';

type Props = {
  id: string;
  contentId: number;
  studentId?: number;
  uniqueViewKey?: string;
  contentType: ActivitiesContentType;
  classId: number;
  canFetch?: boolean;
  parentElement?: HTMLElement | null;
  initialVisibility?: boolean;
  version?: string | null;
  language?: LanguageCodes;
  sectionOverviews?: BaseSectionOverview[];
  headerFooterContent?: HeaderFooterContentBase & {
    pageNumber: number;
    hideCheckPoint?: boolean;
    userName: string;
  };
};

const ActivityBuilderAnswersPagePreview = ({
  id,
  contentId,
  studentId,
  classId,
  uniqueViewKey,
  contentType,
  canFetch = true,
  parentElement,
  initialVisibility,
  version,
  language = LANGUAGE_CODE_EN_AU,
  sectionOverviews,
  headerFooterContent,
}: Props) => {
  const { containerRef, setContainerRef } = usePagePreviewResizeObserver();

  const { isVisible } = usePagePreviewIntersectionObserver({
    containerElement: containerRef,
    initialVisibility,
    parentElement,
  });

  const sectionFromCache = useReadSectionFromCache({
    sectionId: id,
    skip: !version,
    language,
    version,
  });

  const { data, loading } = useGetActivitySections({
    contentId,
    contentType,
    classId,
    sectionIds: [id],
    skip: !!sectionFromCache || !canFetch || !isVisible,
    version,
    language,
    isPublic: false,
  });

  const section = useMemo(() => {
    if (sectionFromCache) return sectionFromCache;
    return (
      data?.getActivitiesSections &&
      getSectionsAndSubSections(data.getActivitiesSections).find(
        s => s.id === id
      )
    );
  }, [data?.getActivitiesSections, id, sectionFromCache]);

  const { pageHeight, contentHeight } = useMemo(
    () =>
      getContentHeight({
        showHeaders: !!headerFooterContent,
        includeDifferentiation: !!sectionOverviews,
      }),
    [headerFooterContent, sectionOverviews]
  );

  return (
    <PagePreviewContent
      ref={setContainerRef}
      dimensions={{ pageHeight, contentHeight }}
      isLoading={loading || !section}
      header={
        headerFooterContent && (
          <ActivityAnswersPrintHeader
            unitName={headerFooterContent.unitName}
            contentName={headerFooterContent.contentName}
            userName={headerFooterContent.userName}
          />
        )
      }
      footer={
        headerFooterContent && (
          <ActivityBuilderIntroFooter
            pageNumber={headerFooterContent.pageNumber}
            subject={headerFooterContent.subjectName}
            showGoogleAttribution={language !== LANGUAGE_CODE_EN_AU}
          />
        )
      }
    >
      {section && sectionOverviews && (
        <ActivityPrint
          section={section}
          sectionsOverview={sectionOverviews}
          hideCheckpoint={!!headerFooterContent?.hideCheckPoint}
        />
      )}
      {section ? (
        <ActivityAnswersPrintComponents
          previewType={`${uniqueViewKey ? `${uniqueViewKey}-` : ''}${PREVIEW}`}
          section={section}
          studentId={studentId}
          language={language}
          onRenderComplete={() => {}}
          contentVersion={version}
        />
      ) : null}
    </PagePreviewContent>
  );
};

export default ActivityBuilderAnswersPagePreview;
